// Physics Unit Tests, Exercises, and Resources
// Vanuatu National Syllabus - Years 11-13

// Assessment Structure
export const physicsAssessment = {
  year11: {
    internalExamination: {
      weight: 60,
      format: "Written examination (3 hours)",
      sections: "Multiple Choice, Short Answer, Problem Solving, Extended Response",
      strandWeights: {
        PHY1: 7,
        PHY2: 12,
        PHY3: 14,
        PHY4: 14,
        PHY5: 13
      }
    },
    continuousAssessment: {
      weight: 40,
      tasks: 5,
      components: [
        { name: "Teacher Design Task (TDT)", count: 2, weight: 16 },
        { name: "Common Assessment Task (CAT)", count: 2, weight: 16 },
        { name: "Practical Investigation", count: 1, weight: 8 }
      ]
    },
    skillLevels: {
      unistructural: "Recall definitions and simple facts",
      multistructural: "Describe, classify, list components",
      relational: "Apply equations, explain relationships",
      extendedAbstract: "Evaluate, design, synthesize"
    }
  },
  year12: {
    externalExamination: {
      weight: 60,
      format: "Written examination (3 hours)",
      sections: "Multiple Choice, Structured Questions, Extended Response",
      totalMarks: 100,
      strandWeights: {
        PHY1: 4,
        PHY2: 14,
        PHY3: 14,
        PHY4: 14,
        PHY5: 14
      }
    },
    internalAssessment: {
      weight: 40,
      tasks: 3,
      components: [
        { name: "Teacher Design Task (TDT)", count: 1, weight: 15 },
        { name: "Common Assessment Task (CAT)", count: 1, weight: 15 },
        { name: "Practical Investigation", count: 1, weight: 10 }
      ]
    }
  },
  year13: {
    externalExamination: {
      weight: 60,
      format: "Written examination (3 hours)",
      totalMarks: 100,
      sections: "Structured Questions, Problem Solving, Extended Analysis",
      strandWeights: {
        PHY1: 5,
        PHY2: 10,
        PHY3: 15,
        PHY4: 15,
        PHY5: 15
      }
    },
    internalAssessment: {
      weight: 40,
      tasks: 3,
      components: [
        { name: "Task 1: Practical Investigation", count: 1, weight: 15 },
        { name: "Task 2: Research Project", count: 1, weight: 15 },
        { name: "Task 3: Problem Set", count: 1, weight: 10 }
      ]
    }
  }
};

// Year 11 Unit Tests
export const year11PhysicsUnitTests = {
  term1: [
    {
      id: "y11-phy-t1-test1",
      title: "Measurements and Vectors Test",
      strand: "PHY1",
      topics: ["SI Units", "Vectors", "Graphical Analysis"],
      duration: "50 minutes",
      totalMarks: 40,
      instructions: [
        "Answer all questions",
        "Show all working clearly",
        "Use appropriate SI units in answers"
      ],
      sections: [
        {
          name: "Section A: Multiple Choice",
          marks: 10,
          questions: [
            { id: 1, question: "The SI base unit for length is:", options: ["A) centimeter", "B) meter", "C) kilometer", "D) millimeter"], marks: 1, answer: "B" },
            { id: 2, question: "Which is a vector quantity?", options: ["A) Speed", "B) Mass", "C) Displacement", "D) Time"], marks: 1, answer: "C" },
            { id: 3, question: "3.5 × 10⁻⁴ in decimal form is:", options: ["A) 0.00035", "B) 0.0035", "C) 35000", "D) 0.000035"], marks: 1, answer: "A" },
            { id: 4, question: "The gradient of a distance-time graph gives:", options: ["A) Acceleration", "B) Speed", "C) Distance", "D) Time"], marks: 1, answer: "B" },
            { id: 5, question: "Two perpendicular vectors of 3N and 4N have a resultant of:", options: ["A) 7N", "B) 1N", "C) 5N", "D) 12N"], marks: 1, answer: "C" }
          ]
        },
        {
          name: "Section B: Short Answer",
          marks: 15,
          questions: [
            { id: 6, question: "Convert 5.2 km to meters.", marks: 2 },
            { id: 7, question: "Express 45,000,000 in scientific notation.", marks: 2 },
            { id: 8, question: "A force of 8N acts at 30° to the horizontal. Calculate the horizontal and vertical components.", marks: 4 },
            { id: 9, question: "Define a vector quantity and give two examples.", marks: 3 },
            { id: 10, question: "List the seven SI base units and their symbols.", marks: 4 }
          ]
        },
        {
          name: "Section C: Problem Solving",
          marks: 15,
          questions: [
            { id: 11, question: "Two forces of 5N and 12N act on an object. Calculate the maximum and minimum possible resultant forces.", marks: 5 },
            { id: 12, question: "A student measures a length as 25.3 cm. The true value is 25.0 cm. Calculate the percentage error.", marks: 4 },
            { id: 13, question: "Draw a displacement-time graph for an object that: (a) is stationary for 2s, (b) moves at constant velocity for 3s, (c) returns to starting point in 2s.", marks: 6 }
          ]
        }
      ]
    },
    {
      id: "y11-phy-t1-test2",
      title: "Kinematics Test",
      strand: "PHY2",
      topics: ["Displacement", "Velocity", "Acceleration", "Kinematic Equations"],
      duration: "55 minutes",
      totalMarks: 45,
      instructions: [
        "Answer all questions",
        "Take g = 10 m/s² unless stated otherwise",
        "Show all working and include units"
      ],
      sections: [
        {
          name: "Section A: Definitions",
          marks: 12,
          questions: [
            { id: 1, question: "Define displacement.", marks: 2 },
            { id: 2, question: "State the difference between speed and velocity.", marks: 3 },
            { id: 3, question: "Define acceleration and state its SI unit.", marks: 3 },
            { id: 4, question: "Write down the four kinematic equations for uniformly accelerated motion.", marks: 4 }
          ]
        },
        {
          name: "Section B: Calculations",
          marks: 20,
          questions: [
            { id: 5, question: "A car accelerates from rest at 2 m/s² for 10 seconds. Calculate: (a) final velocity, (b) distance travelled.", marks: 6 },
            { id: 6, question: "A ball is thrown vertically upward with velocity 20 m/s. Calculate: (a) maximum height, (b) time to reach maximum height, (c) total time of flight.", marks: 8 },
            { id: 7, question: "A train travelling at 30 m/s brakes and comes to rest in 150 m. Calculate the deceleration.", marks: 6 }
          ]
        },
        {
          name: "Section C: Graph Analysis",
          marks: 13,
          questions: [
            { id: 8, question: "From a velocity-time graph showing a straight line from (0,0) to (5s, 20m/s): (a) Calculate the acceleration (b) Calculate the distance travelled (c) Sketch the corresponding acceleration-time graph", marks: 8 },
            { id: 9, question: "Explain why the area under a velocity-time graph gives displacement.", marks: 5 }
          ]
        }
      ]
    }
  ],
  term2: [
    {
      id: "y11-phy-t2-test1",
      title: "Forces and Newton's Laws Test",
      strand: "PHY2",
      topics: ["Newton's Laws", "Free-body Diagrams", "Friction"],
      duration: "60 minutes",
      totalMarks: 50,
      instructions: [
        "Answer all questions",
        "Draw clear free-body diagrams where required",
        "Take g = 10 m/s²"
      ],
      sections: [
        {
          name: "Section A: Theory",
          marks: 15,
          questions: [
            { id: 1, question: "State Newton's First Law of Motion.", marks: 3 },
            { id: 2, question: "State Newton's Second Law and write the equation.", marks: 3 },
            { id: 3, question: "State Newton's Third Law and give an example.", marks: 4 },
            { id: 4, question: "Explain the difference between mass and weight.", marks: 3 },
            { id: 5, question: "Define static friction and kinetic friction.", marks: 2 }
          ]
        },
        {
          name: "Section B: Free-body Diagrams",
          marks: 15,
          questions: [
            { id: 6, question: "Draw a free-body diagram for: (a) A book resting on a table, (b) A ball falling through air.", marks: 6 },
            { id: 7, question: "A 5 kg mass is suspended by two strings at angles of 30° and 60° to the horizontal. Draw the free-body diagram and label all forces.", marks: 5 },
            { id: 8, question: "Draw a free-body diagram for a car accelerating on a horizontal road.", marks: 4 }
          ]
        },
        {
          name: "Section C: Calculations",
          marks: 20,
          questions: [
            { id: 9, question: "A 2000 kg car accelerates at 3 m/s². Calculate the net force acting on the car.", marks: 4 },
            { id: 10, question: "A 10 kg box is pushed along a floor with a force of 50 N. If the friction force is 20 N, calculate: (a) Net force, (b) Acceleration", marks: 6 },
            { id: 11, question: "A 60 kg person stands in a lift. Calculate the normal force when the lift: (a) is stationary, (b) accelerates upward at 2 m/s², (c) accelerates downward at 2 m/s².", marks: 10 }
          ]
        }
      ]
    },
    {
      id: "y11-phy-t2-test2",
      title: "Work, Energy and Power Test",
      strand: "PHY2",
      topics: ["Work", "Kinetic Energy", "Potential Energy", "Power", "Efficiency"],
      duration: "55 minutes",
      totalMarks: 45,
      instructions: [
        "Answer all questions",
        "Show all calculations clearly",
        "Take g = 10 m/s²"
      ],
      sections: [
        {
          name: "Section A: Definitions",
          marks: 12,
          questions: [
            { id: 1, question: "Define work and state its SI unit.", marks: 2 },
            { id: 2, question: "Define kinetic energy and write its formula.", marks: 2 },
            { id: 3, question: "Define gravitational potential energy and write its formula.", marks: 2 },
            { id: 4, question: "State the law of conservation of energy.", marks: 3 },
            { id: 5, question: "Define power and efficiency.", marks: 3 }
          ]
        },
        {
          name: "Section B: Calculations",
          marks: 25,
          questions: [
            { id: 6, question: "A force of 40 N moves a box 5 m along a floor at 30° to the horizontal. Calculate the work done.", marks: 5 },
            { id: 7, question: "A 2 kg ball moving at 10 m/s strikes a wall and bounces back at 8 m/s. Calculate the kinetic energy lost.", marks: 5 },
            { id: 8, question: "A 50 kg person climbs 10 m in 20 seconds. Calculate: (a) Work done against gravity, (b) Power output.", marks: 6 },
            { id: 9, question: "A pendulum bob of mass 0.5 kg is raised 0.2 m. Calculate: (a) PE at maximum height, (b) KE at lowest point, (c) Speed at lowest point.", marks: 9 }
          ]
        },
        {
          name: "Section C: Application",
          marks: 8,
          questions: [
            { id: 10, question: "A machine lifts a 200 kg load through 5 m. The motor provides 15000 J of energy. Calculate the efficiency of the machine.", marks: 8 }
          ]
        }
      ]
    }
  ],
  term3: [
    {
      id: "y11-phy-t3-test1",
      title: "Electricity Test",
      strand: "PHY3",
      topics: ["Current", "Voltage", "Resistance", "Ohm's Law", "Power"],
      duration: "60 minutes",
      totalMarks: 50,
      instructions: [
        "Answer all questions",
        "Draw circuit diagrams clearly",
        "Show all calculations"
      ],
      sections: [
        {
          name: "Section A: Theory",
          marks: 15,
          questions: [
            { id: 1, question: "Define electric current and state its SI unit.", marks: 2 },
            { id: 2, question: "Define potential difference (voltage).", marks: 2 },
            { id: 3, question: "State Ohm's Law and write the equation.", marks: 3 },
            { id: 4, question: "Explain why metals are good conductors of electricity.", marks: 3 },
            { id: 5, question: "State two differences between series and parallel circuits.", marks: 4 },
            { id: 6, question: "Explain how an ammeter and voltmeter should be connected in a circuit.", marks: 1 }
          ]
        },
        {
          name: "Section B: Circuit Analysis",
          marks: 20,
          questions: [
            { id: 7, question: "Three resistors of 2Ω, 3Ω, and 6Ω are connected in series. Calculate the total resistance.", marks: 3 },
            { id: 8, question: "The same three resistors are connected in parallel. Calculate the total resistance.", marks: 5 },
            { id: 9, question: "A circuit has a 12V battery and two resistors (4Ω and 6Ω) in series. Calculate: (a) Total resistance, (b) Current, (c) Voltage across each resistor.", marks: 7 },
            { id: 10, question: "Draw a circuit diagram with a battery, switch, ammeter, and two bulbs in parallel.", marks: 5 }
          ]
        },
        {
          name: "Section C: Power and Energy",
          marks: 15,
          questions: [
            { id: 11, question: "A 60W bulb operates at 240V. Calculate: (a) Current through the bulb, (b) Resistance of the bulb.", marks: 6 },
            { id: 12, question: "A 2kW heater is used for 3 hours. Calculate: (a) Energy consumed in kWh, (b) Cost at 25 vatu per kWh.", marks: 5 },
            { id: 13, question: "Explain why a thin wire has more resistance than a thick wire of the same material and length.", marks: 4 }
          ]
        }
      ]
    }
  ]
};

// Year 12 Unit Tests
export const year12PhysicsUnitTests = {
  term1: [
    {
      id: "y12-phy-t1-test1",
      title: "Projectile and Circular Motion Test",
      strand: "PHY2",
      topics: ["Projectile Motion", "Circular Motion", "Centripetal Force"],
      duration: "70 minutes",
      totalMarks: 60,
      instructions: [
        "Answer all questions",
        "Take g = 10 m/s²",
        "Show all working clearly"
      ],
      sections: [
        {
          name: "Section A: Projectile Motion",
          marks: 25,
          questions: [
            { id: 1, question: "A ball is thrown horizontally at 15 m/s from a cliff 45 m high. Calculate: (a) Time of flight, (b) Horizontal distance travelled, (c) Velocity just before hitting the ground.", marks: 12 },
            { id: 2, question: "A projectile is launched at 40 m/s at 30° above horizontal. Calculate: (a) Horizontal and vertical components of initial velocity, (b) Maximum height, (c) Range.", marks: 13 }
          ]
        },
        {
          name: "Section B: Circular Motion",
          marks: 25,
          questions: [
            { id: 3, question: "Define: (a) Angular velocity, (b) Centripetal acceleration, (c) Centripetal force.", marks: 6 },
            { id: 4, question: "A 1000 kg car goes around a curve of radius 50 m at 20 m/s. Calculate: (a) Centripetal acceleration, (b) Centripetal force required.", marks: 6 },
            { id: 5, question: "A stone of mass 0.2 kg is whirled in a vertical circle of radius 0.8 m. At the top, the speed is 4 m/s. Calculate the tension in the string at the top.", marks: 6 },
            { id: 6, question: "Explain why a car on a banked track can go around a curve faster than on a flat track.", marks: 7 }
          ]
        },
        {
          name: "Section C: Extended Response",
          marks: 10,
          questions: [
            { id: 7, question: "Discuss the differences between projectile motion and circular motion in terms of: (a) Nature of the path, (b) Acceleration, (c) Forces involved.", marks: 10 }
          ]
        }
      ]
    }
  ],
  term2: [
    {
      id: "y12-phy-t2-test1",
      title: "Momentum and Electromagnetic Induction Test",
      strand: "PHY2 & PHY3",
      topics: ["Momentum", "Collisions", "Faraday's Law", "Lenz's Law"],
      duration: "65 minutes",
      totalMarks: 55,
      instructions: [
        "Answer all questions",
        "Show all working",
        "State the direction of induced currents/EMFs"
      ],
      sections: [
        {
          name: "Section A: Momentum",
          marks: 25,
          questions: [
            { id: 1, question: "Define momentum and impulse, stating their SI units.", marks: 4 },
            { id: 2, question: "A 1500 kg car travelling at 20 m/s collides with a stationary 1000 kg car. After the collision, they move together. Calculate: (a) Momentum before collision, (b) Velocity after collision, (c) Kinetic energy lost.", marks: 10 },
            { id: 3, question: "A 0.1 kg ball moving at 5 m/s bounces off a wall and returns at 4 m/s. Calculate: (a) Change in momentum, (b) Impulse on the ball.", marks: 6 },
            { id: 4, question: "Explain why airbags reduce injuries in car crashes using the concept of impulse.", marks: 5 }
          ]
        },
        {
          name: "Section B: Electromagnetic Induction",
          marks: 30,
          questions: [
            { id: 5, question: "State Faraday's Law of electromagnetic induction.", marks: 3 },
            { id: 6, question: "State Lenz's Law and explain its significance.", marks: 4 },
            { id: 7, question: "A coil of 100 turns has a magnetic flux change from 0.05 Wb to 0.02 Wb in 0.1 s. Calculate the induced EMF.", marks: 5 },
            { id: 8, question: "Explain the operation of an AC generator with the aid of a diagram.", marks: 8 },
            { id: 9, question: "A transformer has 500 primary turns and 100 secondary turns. If the primary voltage is 240V, calculate: (a) Secondary voltage, (b) Type of transformer (step-up or step-down).", marks: 5 },
            { id: 10, question: "Explain why the core of a transformer is made of laminated iron sheets.", marks: 5 }
          ]
        }
      ]
    }
  ]
};

// Year 13 Unit Tests
export const year13PhysicsUnitTests = {
  term1: [
    {
      id: "y13-phy-t1-test1",
      title: "Rotational Motion and SHM Test",
      strand: "PHY2",
      topics: ["Angular Motion", "Moment of Inertia", "Simple Harmonic Motion"],
      duration: "75 minutes",
      totalMarks: 65,
      instructions: [
        "Answer all questions",
        "Show all derivations where required",
        "Use standard notation"
      ],
      sections: [
        {
          name: "Section A: Rotational Motion",
          marks: 30,
          questions: [
            { id: 1, question: "Define: (a) Angular displacement, (b) Angular velocity, (c) Angular acceleration, (d) Moment of inertia.", marks: 8 },
            { id: 2, question: "A disc of mass 2 kg and radius 0.3 m rotates at 10 rad/s. Calculate: (a) Moment of inertia (I = ½MR²), (b) Angular momentum, (c) Rotational kinetic energy.", marks: 9 },
            { id: 3, question: "A torque of 5 Nm acts on a wheel with moment of inertia 2 kg⋅m². Calculate the angular acceleration.", marks: 4 },
            { id: 4, question: "An ice skater spinning with arms extended has angular velocity 2 rad/s. When she pulls her arms in, her moment of inertia halves. Calculate her new angular velocity.", marks: 5 },
            { id: 5, question: "Explain why divers tuck their bodies when performing somersaults.", marks: 4 }
          ]
        },
        {
          name: "Section B: Simple Harmonic Motion",
          marks: 35,
          questions: [
            { id: 6, question: "Define simple harmonic motion and state the conditions for SHM.", marks: 5 },
            { id: 7, question: "Write the equations for displacement, velocity, and acceleration in SHM.", marks: 6 },
            { id: 8, question: "A mass-spring system has k = 50 N/m and m = 2 kg. Calculate: (a) Period, (b) Frequency, (c) Angular frequency.", marks: 7 },
            { id: 9, question: "A simple pendulum of length 1 m oscillates with amplitude 5 cm. Calculate: (a) Period, (b) Maximum velocity, (c) Maximum acceleration.", marks: 9 },
            { id: 10, question: "Sketch graphs showing displacement, velocity, and acceleration against time for SHM, noting their phase relationships.", marks: 8 }
          ]
        }
      ]
    }
  ],
  term3: [
    {
      id: "y13-phy-t3-test1",
      title: "Nuclear Physics Test",
      strand: "PHY5",
      topics: ["Binding Energy", "Radioactive Decay", "Nuclear Reactions"],
      duration: "70 minutes",
      totalMarks: 60,
      instructions: [
        "Answer all questions",
        "Use c = 3 × 10⁸ m/s, 1 u = 931.5 MeV/c²",
        "Show all calculations"
      ],
      sections: [
        {
          name: "Section A: Nuclear Structure",
          marks: 15,
          questions: [
            { id: 1, question: "Define: (a) Mass number, (b) Atomic number, (c) Isotopes.", marks: 6 },
            { id: 2, question: "Explain what is meant by mass defect and binding energy.", marks: 4 },
            { id: 3, question: "Why is binding energy per nucleon important for understanding nuclear stability?", marks: 5 }
          ]
        },
        {
          name: "Section B: Radioactive Decay",
          marks: 25,
          questions: [
            { id: 4, question: "Write nuclear equations for: (a) Alpha decay of U-238, (b) Beta decay of C-14, (c) Gamma emission.", marks: 9 },
            { id: 5, question: "A radioactive sample has a half-life of 8 days. If the initial activity is 800 Bq, calculate the activity after: (a) 8 days, (b) 24 days.", marks: 6 },
            { id: 6, question: "The decay constant of a nuclide is 0.1 s⁻¹. Calculate: (a) Half-life, (b) Fraction remaining after 30 seconds.", marks: 6 },
            { id: 7, question: "Explain one medical and one industrial application of radioactive isotopes.", marks: 4 }
          ]
        },
        {
          name: "Section C: Nuclear Reactions",
          marks: 20,
          questions: [
            { id: 8, question: "Distinguish between nuclear fission and nuclear fusion.", marks: 4 },
            { id: 9, question: "Calculate the energy released when 1 u of mass is converted to energy (E = mc²).", marks: 4 },
            { id: 10, question: "Explain why nuclear fusion requires extremely high temperatures to occur.", marks: 4 },
            { id: 11, question: "Discuss the advantages and disadvantages of nuclear power as an energy source.", marks: 8 }
          ]
        }
      ]
    }
  ]
};

// Exercises
export const year11PhysicsExercises = [
  {
    id: "y11-phy-ex1",
    title: "Vector Addition Practice",
    strand: "PHY1",
    type: "Graphing Exercise",
    questions: [
      "Two forces of 4N and 5N act at 60° to each other. Find the resultant graphically.",
      "Resolve a 10N force at 45° into horizontal and vertical components.",
      "Three forces of 3N, 4N, and 5N act at a point. Find conditions for equilibrium."
    ]
  },
  {
    id: "y11-phy-ex2",
    title: "Kinematics Problems",
    strand: "PHY2",
    type: "Calculation Exercise",
    questions: [
      "A car accelerates from 10 m/s to 30 m/s in 5 s. Find acceleration and distance.",
      "A ball is dropped from 80 m. Find time to fall and velocity on impact.",
      "Plot v-t graph for a=2 m/s² from rest for 10s, then constant velocity for 5s."
    ]
  },
  {
    id: "y11-phy-ex3",
    title: "Circuit Analysis Practice",
    strand: "PHY3",
    type: "Problem Solving",
    questions: [
      "Design a circuit with 12V battery giving 3A through a 2Ω and 4Ω series combination.",
      "Calculate equivalent resistance of 6Ω and 3Ω in parallel, then in series with 4Ω.",
      "A 240V appliance draws 10A. Find resistance, power, and energy used in 2 hours."
    ]
  }
];

export const year12PhysicsExercises = [
  {
    id: "y12-phy-ex1",
    title: "Projectile Motion Practice",
    strand: "PHY2",
    type: "Calculation Exercise",
    questions: [
      "A ball thrown at 20 m/s at 45°. Calculate range, maximum height, time of flight.",
      "A bullet fired horizontally at 200 m/s drops 5 cm. Calculate horizontal distance.",
      "Show that maximum range occurs at 45° launch angle."
    ]
  },
  {
    id: "y12-phy-ex2",
    title: "Momentum and Collisions",
    strand: "PHY2",
    type: "Problem Solving",
    questions: [
      "A 2 kg ball at 5 m/s collides elastically with stationary 1 kg ball. Find final velocities.",
      "A 1000 kg car at 20 m/s collides with 2000 kg truck at 10 m/s (same direction). Find final velocity.",
      "Calculate the impulse needed to stop a 60 kg person running at 8 m/s in 0.5 s."
    ]
  }
];

export const year13PhysicsExercises = [
  {
    id: "y13-phy-ex1",
    title: "SHM Practice",
    strand: "PHY2",
    type: "Calculation Exercise",
    questions: [
      "A 0.5 kg mass on a spring (k=200 N/m) oscillates with 10 cm amplitude. Find period, max velocity, max acceleration.",
      "A pendulum of length 0.5 m. Calculate period on Earth and on Moon (g=1.6 m/s²).",
      "Derive the expression for total energy in SHM and show it is constant."
    ]
  },
  {
    id: "y13-phy-ex2",
    title: "Nuclear Calculations",
    strand: "PHY5",
    type: "Problem Solving",
    questions: [
      "Calculate binding energy per nucleon for He-4 (mass = 4.0026 u).",
      "Ra-226 has half-life 1600 years. Calculate decay constant and activity of 1 mg sample.",
      "Calculate energy released in fusion: 4¹H → ⁴He + 2e⁺ + 2ν + energy."
    ]
  }
];

// Resources
export const physicsResources = [
  {
    title: "Physics Formula Sheet",
    type: "Reference Card",
    description: "All essential formulas for Years 11-13",
    unit: "All Strands",
    year: "All Years"
  },
  {
    title: "SI Units and Prefixes",
    type: "Reference Card",
    description: "Base units, derived units, and metric prefixes",
    unit: "PHY1",
    year: "Year 11"
  },
  {
    title: "Circuit Diagram Symbols",
    type: "Reference Chart",
    description: "Standard electrical symbols for circuit diagrams",
    unit: "PHY3",
    year: "Year 11-12"
  },
  {
    title: "Nuclear Decay Chart",
    type: "Reference",
    description: "Alpha, beta, gamma decay comparison and equations",
    unit: "PHY5",
    year: "Year 11-13"
  },
  {
    title: "Past Examination Papers",
    type: "Practice Papers",
    description: "Collection of past SPBEA physics papers",
    unit: "All",
    year: "Year 12-13"
  },
  {
    title: "Laboratory Manual",
    type: "Practical Guide",
    description: "Standard physics experiments and procedures",
    unit: "All",
    year: "All Years"
  },
  {
    title: "Physics Glossary",
    type: "Glossary",
    description: "Key terms and definitions for each strand",
    unit: "All",
    year: "All Years"
  },
  {
    title: "Problem-Solving Strategies",
    type: "Study Guide",
    description: "Step-by-step approaches to physics problems",
    unit: "All",
    year: "All Years"
  }
];

export default {
  physicsAssessment,
  year11PhysicsUnitTests,
  year12PhysicsUnitTests,
  year13PhysicsUnitTests,
  year11PhysicsExercises,
  year12PhysicsExercises,
  year13PhysicsExercises,
  physicsResources
};
